import { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import {
  useInitialBoardLoad,
  useMoveCardMutation,
  useUpdateCardMutation,
  useReorderColumnsMutation,
  useDeleteCardMutation,
  useRenameColumnMutation,
  useDeleteColumnMutation,
  useTaskStagesQuery,
  usePracticeAreaQuery,
  useUsersQuery,
  useCreateTaskStageMutation,
  useTaskActionsMutation,
  useCreateTaskMutation,
  useUpdateFullTaskMutation,
  useDeleteTaskMutation,
  useUpdateSubtaskMutation,
  useCaseClientsQuery,
} from '../queries';
import { DELAYS } from '../../utils/constants';

export const useLazyBoardWithQuery = (filters = {}) => {
  // Use useRef for lock management to avoid race conditions and unnecessary rerenders
  const lockedEntitiesRef = useRef(new Set());
  const [lockedEntitiesVersion, setLockedEntitiesVersion] = useState(0); // Force rerenders when locks change

  const { practiceAreaList, isPracticeAreaLoading } = usePracticeAreaQuery({ loadFullList: true, status: 'active' });
  const { userList, isUserLoading } = useUsersQuery({ status: 'ACTIVE', excludeFromLoading: true, enabled: !filters.case_id });
  const { clientList, isLoading: isLoadingClients } = useCaseClientsQuery({
    caseId: filters.case_id || undefined,
    excludeFromLoading: true,
    enabled: !!filters.case_id,
    is_mandatory_lawft_access: true,
  });

  const { taskStages, isTaskStageLoading: isLoadingTaskStages, error: taskStagesError } = useTaskStagesQuery();

  const { isFetching: isFetchingInitialLoad, error: initialLoadError, refetch: resetBoard } = useInitialBoardLoad(taskStages, filters);

  const initialLoading = isLoadingTaskStages;

  const isRefetchingData = isFetchingInitialLoad;

  const error = taskStagesError || initialLoadError;

  const moveCardMutation = useMoveCardMutation();
  const updateCardMutation = useUpdateCardMutation();
  const reorderColumnsMutation = useReorderColumnsMutation();
  const deleteCardMutation = useDeleteCardMutation();
  const renameColumnMutation = useRenameColumnMutation();
  const deleteColumnMutation = useDeleteColumnMutation();
  const createTaskStageMutation = useCreateTaskStageMutation();
  const { removeTaskMutation, moveTaskToStageMutation, findDoneStage } = useTaskActionsMutation(taskStages);

  // Task CRUD mutations
  const createTaskMutation = useCreateTaskMutation();
  const updateFullTaskMutation = useUpdateFullTaskMutation();
  const deleteTaskMutation = useDeleteTaskMutation();
  const updateSubtaskMutation = useUpdateSubtaskMutation();

  // Helper function to auto-reset mutation errors after a delay
  const useMutationErrorReset = (mutation, delay = DELAYS.MUTATION_ERROR_RESET) => {
    useEffect(() => {
      if (mutation.isError) {
        const timer = setTimeout(() => mutation.reset(), delay);
        return () => clearTimeout(timer);
      }
    }, [mutation.isError, mutation, delay]);
  };

  // Auto-reset all mutation errors after delay
  useMutationErrorReset(moveCardMutation);
  useMutationErrorReset(updateCardMutation);
  useMutationErrorReset(reorderColumnsMutation);
  useMutationErrorReset(deleteCardMutation);
  useMutationErrorReset(renameColumnMutation);
  useMutationErrorReset(deleteColumnMutation);
  useMutationErrorReset(createTaskStageMutation);
  useMutationErrorReset(removeTaskMutation);
  useMutationErrorReset(moveTaskToStageMutation);
  useMutationErrorReset(createTaskMutation);
  useMutationErrorReset(updateFullTaskMutation);
  useMutationErrorReset(deleteTaskMutation);
  useMutationErrorReset(updateSubtaskMutation);

  const moveCard = useCallback(
    async (dropData) => {
      const { sourceCardId } = dropData;
      const entityId = `task-${sourceCardId}`;

      // Check if entity is locked (atomic check with ref)
      if (lockedEntitiesRef.current.has(entityId)) {
        console.warn(`Card ${sourceCardId} is currently syncing. Operation rejected.`);
        return { success: false, locked: true };
      }

      // Lock entity (atomic operation with ref)
      lockedEntitiesRef.current.add(entityId);
      setLockedEntitiesVersion((v) => v + 1); // Force rerender for UI updates

      try {
        await moveCardMutation.mutateAsync({ ...dropData, filters });
        return { success: true };
      } catch (error) {
        return { success: false, error };
      } finally {
        // Unlock entity after a short delay for UX
        setTimeout(() => {
          lockedEntitiesRef.current.delete(entityId);
          setLockedEntitiesVersion((v) => v + 1); // Force rerender for UI updates
        }, DELAYS.ENTITY_UNLOCK);
      }
    },
    [moveCardMutation, filters]
  );

  const reorderColumns = useCallback(
    async (reorderData) => {
      const { sourceIndex } = reorderData;

      if (!taskStages?.[sourceIndex]) {
        return { success: false, error: new Error('Column not found') };
      }

      const column = taskStages[sourceIndex];
      const entityId = `column-${column.id}`;

      // Prevent reordering of locked columns
      if (column.isLocked) {
        console.warn(`Column "${column.title}" is locked and cannot be reordered.`);
        return { success: false, locked: true };
      }

      // Check if entity is locked (atomic check with ref)
      if (lockedEntitiesRef.current.has(entityId)) {
        console.warn(`Column ${column.id} is currently syncing. Operation rejected.`);
        return { success: false, locked: true };
      }

      // Lock entity (atomic operation with ref)
      lockedEntitiesRef.current.add(entityId);
      setLockedEntitiesVersion((v) => v + 1); // Force rerender for UI updates

      try {
        await reorderColumnsMutation.mutateAsync({ reorderData, taskStages });
        return { success: true };
      } catch (error) {
        return { success: false, error };
      } finally {
        setTimeout(() => {
          lockedEntitiesRef.current.delete(entityId);
          setLockedEntitiesVersion((v) => v + 1); // Force rerender for UI updates
        }, DELAYS.ENTITY_UNLOCK);
      }
    },
    [taskStages, reorderColumnsMutation]
  );

  const updateCard = useCallback(
    async (cardId, columnId, updates) => {
      const entityId = `task-${cardId}`;

      if (lockedEntitiesRef.current.has(entityId)) {
        console.warn(`Card ${cardId} is currently syncing. Operation rejected.`);
        return { success: false, locked: true };
      }

      lockedEntitiesRef.current.add(entityId);
      setLockedEntitiesVersion((v) => v + 1);

      try {
        // Pass data and filters to match mutation signature
        await updateCardMutation.mutateAsync({ cardId, columnId, data: updates, filters });
        return { success: true };
      } catch (error) {
        return { success: false, error };
      } finally {
        setTimeout(() => {
          lockedEntitiesRef.current.delete(entityId);
          setLockedEntitiesVersion((v) => v + 1);
        }, 500);
      }
    },
    [updateCardMutation, filters]
  );

  const moveCardToStage = useCallback(
    async (cardId, sourceColumnId, targetColumnId, position = 'end') => {
      return await moveCard({
        sourceCardId: cardId,
        sourceColumnId,
        targetColumnId,
        position,
      });
    },
    [moveCard]
  );

  const renameColumn = useCallback(
    async (data) => {
      const entityId = `column-${data.task_stage_id}`;

      if (lockedEntitiesRef.current.has(entityId)) {
        console.warn(`Column ${data.task_stage_id} is currently syncing. Operation rejected.`);
        return { success: false, locked: true };
      }

      lockedEntitiesRef.current.add(entityId);
      setLockedEntitiesVersion((v) => v + 1);

      try {
        await renameColumnMutation.mutateAsync(data);
        return { success: true };
      } catch (error) {
        return { success: false, error };
      } finally {
        setTimeout(() => {
          lockedEntitiesRef.current.delete(entityId);
          setLockedEntitiesVersion((v) => v + 1);
        }, 500);
      }
    },
    [renameColumnMutation]
  );

  const deleteColumn = useCallback(
    async (columnId, pk) => {
      const entityId = `column-${columnId}`;

      if (lockedEntitiesRef.current.has(entityId)) {
        console.warn(`Column ${columnId} is currently syncing. Operation rejected.`);
        return { success: false, locked: true };
      }

      lockedEntitiesRef.current.add(entityId);
      setLockedEntitiesVersion((v) => v + 1);

      try {
        await deleteColumnMutation.mutateAsync({ columnId, pk });
        return { success: true };
      } catch (error) {
        return { success: false, error };
      } finally {
        setTimeout(() => {
          lockedEntitiesRef.current.delete(entityId);
          setLockedEntitiesVersion((v) => v + 1);
        }, 500);
      }
    },
    [deleteColumnMutation]
  );

  const createTaskStage = useCallback(
    async (data) => {
      try {
        await createTaskStageMutation.mutateAsync(data);
        return { success: true };
      } catch (error) {
        return { success: false, error };
      }
    },
    [createTaskStageMutation]
  );

  const duplicateColumn = useCallback(
    async (column) => {
      if (!column?.task_stage_name) {
        return { success: false, error: new Error('Invalid column data') };
      }

      const entityId = `column-duplicate-${column.task_stage_id}`;

      if (lockedEntitiesRef.current.has(entityId)) {
        console.warn(`Column ${column.task_stage_id} is currently being duplicated. Operation rejected.`);
        return { success: false, locked: true };
      }

      lockedEntitiesRef.current.add(entityId);
      setLockedEntitiesVersion((v) => v + 1);

      try {
        // Generate a unique stage name
        let duplicateName = `${column.task_stage_name} duplicate`;

        // Create the duplicate stage
        await createTaskStageMutation.mutateAsync({
          task_stage_name: duplicateName,
        });

        return { success: true, stageName: duplicateName };
      } catch (error) {
        return { success: false, error };
      } finally {
        setTimeout(() => {
          lockedEntitiesRef.current.delete(entityId);
          setLockedEntitiesVersion((v) => v + 1);
        }, 500);
      }
    },
    [createTaskStageMutation, taskStages]
  );

  // Helper function to check if a stage name already exists
  const checkStageExists = useCallback(
    (stageName) => {
      if (!stageName || !taskStages) return false;
      const normalizedStageName = stageName.trim().toLowerCase();
      return taskStages.some((stage) => stage.task_stage_name.toLowerCase() === normalizedStageName);
    },
    [taskStages]
  );

  /**
   * Remove a task from the board (used after deleting or archiving)
   * @param {string} taskId - The task's sk
   * @param {string} columnId - The column the task is currently in
   */
  const removeTask = useCallback(
    (taskId, columnId) => {
      removeTaskMutation.mutate({
        taskId,
        columnId,
        filters,
      });
    },
    [removeTaskMutation, filters]
  );

  /**
   * Move a task to the done stage (used after completing a task)
   * @param {string} taskId - The task's sk
   * @param {string} sourceColumnId - The column the task is currently in
   */
  const moveTaskToDoneStage = useCallback(
    (taskId, sourceColumnId) => {
      const doneStage = findDoneStage();

      if (doneStage && sourceColumnId !== doneStage.task_stage_id) {
        moveTaskToStageMutation.mutate({
          taskId,
          sourceColumnId,
          targetColumnId: doneStage.task_stage_id,
          filters,
        });
      }
    },
    [moveTaskToStageMutation, findDoneStage, filters]
  );

  const createTask = useCallback(
    async (data, is_lead = false) => {
      try {
        const result = await createTaskMutation.mutateAsync({ data, is_lead });
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to create task:', error);
        return { success: false, error };
      }
    },
    [createTaskMutation]
  );

  const updateFullTask = useCallback(
    async (taskId, data, isReminderUpdated = false, is_lead = false) => {
      try {
        const result = await updateFullTaskMutation.mutateAsync({ taskId, data, isReminderUpdated, is_lead });
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to update task:', error);
        return { success: false, error };
      }
    },
    [updateFullTaskMutation]
  );

  const deleteTask = useCallback(
    async (taskId, caseId, pk, is_lead = false) => {
      try {
        const result = await deleteTaskMutation.mutateAsync({ taskId, caseId, pk, is_lead });
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to delete task:', error);
        return { success: false, error };
      }
    },
    [deleteTaskMutation]
  );

  const updateSubtask = useCallback(
    async (taskId, subTaskIndex, checked, parentTask, currentColumnId) => {
      try {
        const result = await updateSubtaskMutation.mutateAsync({
          taskId,
          subTaskIndex,
          checked,
          parentTask,
          columnId: currentColumnId,
          taskStages,
        });
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to update subtask:', error);
        return { success: false, error };
      }
    },
    [updateSubtaskMutation, taskStages]
  );

  // Convert lockedEntities Set to Array for component compatibility - memoized to prevent reference changes
  const lockedEntitiesArray = useMemo(() => Array.from(lockedEntitiesRef.current), [lockedEntitiesVersion]);

  return {
    practiceAreaList,
    isPracticeAreaLoading,
    clientList,
    isLoadingClients,
    userList,
    isLoadingUsers: isUserLoading,
    isLoadingTaskStages,
    initialLoading,
    isRefetchingData, // Expose for showing skeleton loaders during filter changes
    error: error?.message || error || null,
    moveCard,
    reorderColumns,
    resetBoard,
    taskStages,
    // Operation status tracking with both pending and failed states
    operationStatus: new Map([
      // Move card operation
      ...(moveCardMutation.isLoading ? [['moveCard', { status: 'pending' }]] : []),
      ...(moveCardMutation.isError ? [['moveCard-error', { status: 'failed', error: moveCardMutation.error }]] : []),

      // Update card operation
      ...(updateCardMutation.isLoading ? [['updateCard', { status: 'pending' }]] : []),
      ...(updateCardMutation.isError ? [['updateCard-error', { status: 'failed', error: updateCardMutation.error }]] : []),

      // Reorder columns operation
      ...(reorderColumnsMutation.isLoading ? [['reorderColumns', { status: 'pending' }]] : []),
      ...(reorderColumnsMutation.isError ? [['reorderColumns-error', { status: 'failed', error: reorderColumnsMutation.error }]] : []),

      // Rename column operation
      ...(renameColumnMutation.isLoading ? [['renameColumn', { status: 'pending' }]] : []),
      ...(renameColumnMutation.isError ? [['renameColumn-error', { status: 'failed', error: renameColumnMutation.error }]] : []),

      // Archive column operation
      ...(deleteColumnMutation.isLoading ? [['deleteColumn', { status: 'pending' }]] : []),
      ...(deleteColumnMutation.isError ? [['deleteColumn-error', { status: 'failed', error: deleteColumnMutation.error }]] : []),

      // Create task stage operation
      ...(createTaskStageMutation.isLoading ? [['createTaskStage', { status: 'pending' }]] : []),
      ...(createTaskStageMutation.isError ? [['createTaskStage-error', { status: 'failed', error: createTaskStageMutation.error }]] : []),

      // Remove task operation (delete/archive)
      ...(removeTaskMutation.isLoading ? [['removeTask', { status: 'pending' }]] : []),
      ...(removeTaskMutation.isError ? [['removeTask-error', { status: 'failed', error: removeTaskMutation.error }]] : []),

      // Move task to done stage operation
      ...(moveTaskToStageMutation.isLoading ? [['moveTaskToDoneStage', { status: 'pending' }]] : []),
      ...(moveTaskToStageMutation.isError
        ? [['moveTaskToDoneStage-error', { status: 'failed', error: moveTaskToStageMutation.error }]]
        : []),

      // Task CRUD operations
      ...(createTaskMutation.isLoading ? [['createTask', { status: 'pending' }]] : []),
      ...(createTaskMutation.isError ? [['createTask-error', { status: 'failed', error: createTaskMutation.error }]] : []),

      ...(updateFullTaskMutation.isLoading ? [['updateFullTask', { status: 'pending' }]] : []),
      ...(updateFullTaskMutation.isError ? [['updateFullTask-error', { status: 'failed', error: updateFullTaskMutation.error }]] : []),

      ...(deleteTaskMutation.isLoading ? [['deleteTask', { status: 'pending' }]] : []),
      ...(deleteTaskMutation.isError ? [['deleteTask-error', { status: 'failed', error: deleteTaskMutation.error }]] : []),

      // Update subtask operation
      ...(updateSubtaskMutation.isLoading ? [['updateSubtask', { status: 'pending' }]] : []),
      ...(updateSubtaskMutation.isError ? [['updateSubtask-error', { status: 'failed', error: updateSubtaskMutation.error }]] : []),
    ]),
    hasPendingOperations: () =>
      moveCardMutation.isLoading ||
      updateCardMutation.isLoading ||
      reorderColumnsMutation.isLoading ||
      deleteCardMutation.isLoading ||
      renameColumnMutation.isLoading ||
      deleteColumnMutation.isLoading ||
      createTaskStageMutation.isLoading ||
      removeTaskMutation.isLoading ||
      moveTaskToStageMutation.isLoading ||
      createTaskMutation.isLoading ||
      updateFullTaskMutation.isLoading ||
      deleteTaskMutation.isLoading ||
      updateSubtaskMutation.isLoading,
    isEntityLocked: (entityId) => lockedEntitiesRef.current.has(entityId),
    lockedEntities: lockedEntitiesArray,
    // Card operations
    updateCard,
    moveCardToStage,
    // Column operations
    renameColumn,
    deleteColumn,
    duplicateColumn,
    createTaskStage,
    checkStageExists,
    removeTask,
    moveTaskToDoneStage,
    findDoneStage,
    // Task CRUD operations
    createTask,
    updateFullTask,
    deleteTask,
    updateSubtask,
  };
};
